import * as React from 'react';
import { WuwEventTheme } from '@packages/types';

type Rgb = { r: number; g: number; b: number };

function hexToRgb(hex: string): Rgb | null {
  const m = hex.trim().replace('#', '');
  const full =
    m.length === 3
      ? m
          .split('')
          .map((c) => c + c)
          .join('')
      : m;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/** Relative luminance (0..1) of a #rrggbb / #rgb color, for contrast decisions. */
function luminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a: string, b: string): number | null {
  const lumA = luminance(a);
  const lumB = luminance(b);
  if (lumA === null || lumB === null) return null;
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

function readableAccent(accent: string, bg: string | undefined, ink: string | undefined) {
  if (!bg || !ink) return accent;
  const accentContrast = contrastRatio(accent, bg);
  const inkContrast = contrastRatio(ink, bg);
  // Small mono text needs real WCAG contrast. If the theme accent is too close
  // to the background, keep the palette for large UI but use ink for tiny copy.
  if (accentContrast !== null && accentContrast >= 4.5) return accent;
  if (inkContrast !== null && inkContrast >= 4.5) return ink;
  return accent;
}

/**
 * Map an event's optional theme onto the microsite `--ev-*` CSS variables.
 * Surface / line / muted are derived from background + ink via color-mix so any
 * palette stays cohesive, and the text color auto-flips for readable contrast
 * when no explicit foreground is given. Unset vars fall back to `.ev-root`.
 */
export function resolveEventTheme(theme?: WuwEventTheme): React.CSSProperties {
  const vars: Record<string, string> = {};

  if (theme?.background) {
    vars['--ev-bg'] = theme.background;
    vars['--ev-surface'] = `color-mix(in srgb, ${theme.background} 86%, var(--ev-ink))`;
  }

  // Foreground: explicit wins; otherwise pick light/dark ink based on bg luminance.
  let ink = theme?.foreground;
  if (!ink && theme?.background) {
    const lum = luminance(theme.background);
    if (lum !== null) ink = lum < 0.5 ? '#ECECE6' : '#1B1812';
  }
  if (ink) {
    vars['--ev-ink'] = ink;
    vars['--ev-line'] = `color-mix(in srgb, ${ink} 18%, transparent)`;
    vars['--ev-muted'] = theme?.background
      ? `color-mix(in srgb, ${ink} 72%, ${theme.background})`
      : `color-mix(in srgb, ${ink} 72%, transparent)`;
  }

  if (theme?.primary) {
    vars['--ev-accent'] = theme.primary;
    vars['--ev-accent-readable'] = readableAccent(theme.primary, theme?.background, ink);
    const lum = luminance(theme.primary);
    if (lum !== null) vars['--ev-accent-ink'] = lum < 0.5 ? '#ECECE6' : '#1B1812';
  }
  if (theme?.accent) {
    vars['--ev-accent2'] = theme.accent;
    vars['--ev-accent2-readable'] = readableAccent(theme.accent, theme?.background, ink);
    const lum = luminance(theme.accent);
    if (lum !== null) vars['--ev-accent2-ink'] = lum < 0.5 ? '#ECECE6' : '#1B1812';
  }

  return vars as React.CSSProperties;
}
